/**
 * Issuance editor — create a draft, build its lines, then issue it.
 *
 * Functional spec: `NewIssuanceDialog`, `IssuanceLineDialog`,
 * `IssuancePickerDialog` and `IssuanceEditor` in `qt_poc/customers_qt.py`.
 *
 * Two things the desktop does that are easy to lose:
 *
 *   1. **Lines come from the catalog, one per ACTIVE component**, with the
 *      quantity multiplied by `default_qty_ratio` and the unit cost defaulting
 *      to that component's effective (post-discount) price. Picking a parent
 *      item is a shorthand for issuing its components.
 *   2. **The line tariff defaults to the issuance's engineering tariff**, which
 *      itself comes from the budget. It is per-line so it can be overridden.
 *
 * The currency is never chosen here: the server forces it to the budget's, and
 * an issuance denominated differently from the budget it draws on makes the
 * balance arithmetic meaningless.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  ActionIcon, Alert, Box, Button, Checkbox, Group, NumberInput, Select, Stack,
  Table, Text, TextInput, Textarea, Title,
} from '@mantine/core'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'

import { ApiError, api } from '../api/client'
import type {
  CustomerBudget, InventorySnapshot, Issuance, IssuanceLine, ItemDetail, ItemRow,
} from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { Modal } from '../components/AppModal'
import { STICKY_END, STICKY_START } from '../components/DataGrid'
import { EM_DASH, fmtMoney, fmtQty } from '../format'
import { CODE_STYLE } from '../theme'
import { ItemPicker } from './ItemPicker'

function blank(customerId: number, budget: CustomerBudget): Issuance {
  return {
    id: null, issuance_number: '', customer_id: customerId, budget_id: budget.id!,
    project_name: '', site_name: '',
    issuance_date: new Date().toISOString().slice(0, 10),
    status: 'DRAFT',
    engineering_tariff_pct: budget.engineering_tariff_pct ?? 0,
    currency: budget.currency, total_equipment_cost: 0, total_tariff: 0,
    total_value: 0, exchange_rate_used: 0, document_path: '',
    corrects_issuance_id: null, notes: '', lines: [],
    created_at: '', updated_at: '',
  }
}

interface Props {
  opened: boolean
  onClose: () => void
  customerId: number
  budgets: CustomerBudget[]
  /** The budget selected on the screen behind — a new draft defaults to it. */
  preferredBudgetId: number | null
  /** null = create a new draft. */
  issuance: Issuance | null
}

export function IssuanceEditor({
  opened, onClose, customerId, budgets, preferredBudgetId, issuance,
}: Props) {
  const qc = useQueryClient()
  // Opening this modal is not itself gated on `canEdit` — a viewer can click
  // an issuance row on the screen behind to look at it read-only, same as
  // everything else in the app. But unlike the rest of the app, an
  // in-progress DRAFT here is not fully locked (`locked` below only tracks
  // status), so without this the add-items/edit affordances would render for
  // a viewer too. That matters beyond the usual "hidden button is a courtesy"
  // case (CLAUDE.md §5): "בחר פריטים מהקטלוג" expands a picked item's
  // components, and each component's `unit_price_usd` is null for a viewer
  // (DP-01) — `null * (1 - discount)` coerces to a real `0`, so a viewer who
  // could reach that flow would build a line that reads as a free component
  // instead of one whose price is simply hidden from them.
  const { canEdit } = useAuth()
  const [draft, setDraft] = useState<Issuance | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Informational, not a failure — kept apart from `error` so "3 lines added"
  // never shows up in a red alert.
  const [notice, setNotice] = useState<string | null>(null)
  const [picking, setPicking] = useState(false)
  const [includeRetired, setIncludeRetired] = useState(false)
  // Fetching the picked items' components is a network round trip, so it says
  // so. Silent work that takes a moment reads as a button that did nothing.
  const [expanding, setExpanding] = useState(false)

  const active = useMemo(
    () => budgets.filter((b) => b.status === 'ACTIVE' || b.id === issuance?.budget_id),
    [budgets, issuance],
  )

  // Stock on hand, so the person issuing can see whether they actually have it.
  // Read-only context: the desktop does not block on stock either, because an
  // issuance can legitimately be raised before the goods land.
  const snapshots = useQuery({
    queryKey: ['snapshots'],
    queryFn: () => api.get<InventorySnapshot[]>('/inventory/snapshots'),
    enabled: opened,
  })
  const latest = snapshots.data?.[0]
  const stock = useQuery({
    queryKey: ['snapshot', latest?.id],
    queryFn: () => api.get<InventorySnapshot>(`/inventory/snapshots/${latest!.id}`),
    enabled: opened && latest?.id != null,
  })
  const stockBySku = useMemo(() => {
    const map = new Map<string, number>()
    for (const line of stock.data?.lines ?? []) {
      map.set(line.sku, (map.get(line.sku) ?? 0) + line.qty_on_hand)
    }
    return map
  }, [stock.data])

  useEffect(() => {
    if (!opened) return
    setError(null)
    setNotice(null)
    if (issuance) {
      setDraft(structuredClone(issuance))
      return
    }
    const preferred = active.find((b) => b.id === preferredBudgetId) ?? active[0]
    setDraft(preferred ? blank(customerId, preferred) : null)
  }, [opened, issuance, customerId, preferredBudgetId, active])

  const save = useMutation({
    mutationFn: (value: Issuance) =>
      value.id == null
        ? api.post<Issuance>('/issuances', value)
        : api.put<Issuance>(`/issuances/${value.id}`, value),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['issuances'] })
      void qc.invalidateQueries({ queryKey: ['balance'] })
      notifications.show({ message: 'הניפוק נשמר', color: 'green' })
      onClose()
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'שמירת הניפוק נכשלה.'),
  })

  function update(patch: Partial<Issuance>) {
    setDraft((d) => (d ? { ...d, ...patch } : d))
  }

  function updateLine(index: number, patch: Partial<IssuanceLine>) {
    setDraft((d) =>
      d ? { ...d, lines: d.lines.map((l, i) => (i === index ? { ...l, ...patch } : l)) } : d,
    )
  }

  /**
   * Expand chosen catalog items into one line per component.
   *
   * The desktop takes ACTIVE components only, and that is the default here —
   * but it does it **silently**, and a component's sale status is not visible
   * while picking. Measured against the local catalog, 2 of 10 items have no
   * ACTIVE component at all: picking one of those adds nothing and says
   * nothing, which reads as a broken button.
   *
   * So the exclusion is reported, and `includeRetired` can lift it. Issuing
   * EOS stock is a real thing to want — end of *sale* does not mean it is not
   * in the warehouse — and the alternative is a user who cannot issue what
   * they are holding and cannot see why.
   *
   * The components come from `GET /items/{id}` per picked item rather than off
   * the grid row: the list endpoint stopped sending component arrays because
   * doing so cost 5.2MB across the catalog, to serve exactly this function and
   * one grid column. A pick is a handful of items, so a few small requests are
   * far cheaper than that — and they run in parallel.
   */
  async function addItems(items: ItemRow[]) {
    setPicking(false)
    if (!draft) return

    let details: ItemDetail[]
    try {
      setExpanding(true)
      details = await Promise.all(
        items.map((i) => api.get<ItemDetail>(`/items/${i.id}`)),
      )
    } catch (err) {
      setNotice(null)
      setError(err instanceof ApiError ? err.message : 'טעינת רכיבי הפריטים נכשלה')
      return
    } finally {
      setExpanding(false)
    }

    const seen = new Set(draft.lines.map((l) => l.sku))
    const added: IssuanceLine[] = []
    let retired = 0
    let duplicates = 0
    for (const item of details) {
      for (const component of item.components) {
        const isRetired = !!component.sale_status && component.sale_status !== 'ACTIVE'
        if (isRetired && !includeRetired) { retired += 1; continue }
        if (seen.has(component.sku)) { duplicates += 1; continue }
        seen.add(component.sku)
        added.push({
          id: null, issuance_id: draft.id, item_id: item.id,
          sku: component.sku, internal_sku: component.internal_sku,
          description: component.description,
          qty: component.default_qty_ratio || 1,
          // Effective price = list minus the component's discount, matching
          // `Component.effective_price_usd` on the desktop.
          //
          // `?? 0` guards a type that is `number | null` because a viewer's
          // copy of this field is null (DP-01) — it never actually fires here
          // since this whole flow is `canEdit`-gated above (a viewer's
          // `unit_price_usd` would otherwise coerce to a real `0` and build a
          // line that reads as a free component).
          unit_cost: (component.unit_price_usd ?? 0) * (1 - (component.discount_pct || 0)),
          line_tariff_pct: draft.engineering_tariff_pct,
        })
      }
    }

    const notes = [
      retired && `${retired} רכיבים אינם פעילים (EOS/EOL) ולא נוספו`,
      duplicates && `${duplicates} רכיבים כבר קיימים בניפוק`,
    ].filter(Boolean).join(' · ')

    if (!added.length) {
      setNotice(null)
      setError(notes
        ? `לא נוספו שורות — ${notes}. סמנו "כלול רכיבים שאינם פעילים" כדי להוסיף אותם.`
        : 'לא נוספו שורות — לפריטים שנבחרו אין רכיבים.')
      return
    }
    setError(null)
    setNotice(notes ? `נוספו ${added.length} שורות · ${notes}` : null)
    setDraft({ ...draft, lines: [...draft.lines, ...added] })
  }

  const totals = useMemo(() => {
    const lines = draft?.lines ?? []
    const equipment = lines.reduce((sum, l) => sum + l.qty * l.unit_cost, 0)
    const tariff = lines.reduce((sum, l) => sum + l.qty * l.unit_cost * (l.line_tariff_pct || 0), 0)
    return { equipment, tariff, total: equipment + tariff }
  }, [draft])

  function submit() {
    setError(null)
    if (!draft) return
    if (!draft.budget_id) return setError('יש לבחור תקציב.')
    const lines = draft.lines.filter((l) => l.sku.trim() && l.qty > 0)
    if (!lines.length) return setError('יש להוסיף לפחות שורה אחת עם כמות.')
    save.mutate({ ...draft, lines })
  }

  const locked = !!draft && draft.status !== 'DRAFT'

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        size="xl"
        title={issuance ? `עריכת ניפוק — ${issuance.issuance_number}` : 'ניפוק חדש (טיוטה)'}
      >
        {!draft ? (
          <Alert color="yellow" variant="light">
            אין תקציב פעיל ללקוח זה. צור תקציב לפני יצירת ניפוק.
          </Alert>
        ) : (
          <Stack>
            {error && <Alert color="red" variant="light">{error}</Alert>}
            {notice && <Alert color="blue" variant="light">{notice}</Alert>}
            {locked && (
              <Alert color="teal" variant="light">
                ניפוק שהונפק אינו ניתן לעריכה — הסכומים ושער החליפין הוקפאו.
              </Alert>
            )}

            <Group grow>
              <Select
                label="תקציב"
                required
                disabled={locked || issuance != null || !canEdit}
                data={active.map((b) => ({
                  value: String(b.id), label: `${b.name} (${b.currency})`,
                }))}
                value={draft.budget_id ? String(draft.budget_id) : null}
                onChange={(v) => {
                  const budget = active.find((b) => String(b.id) === v)
                  if (budget) {
                    update({
                      budget_id: budget.id!,
                      currency: budget.currency,
                      engineering_tariff_pct: budget.engineering_tariff_pct ?? 0,
                    })
                  }
                }}
              />
              <TextInput
                label="תאריך"
                type="date"
                disabled={locked || !canEdit}
                value={draft.issuance_date}
                onChange={(e) => update({ issuance_date: e.currentTarget.value })}
              />
            </Group>

            <Group grow>
              <TextInput
                label="פרויקט" disabled={locked || !canEdit} value={draft.project_name}
                onChange={(e) => update({ project_name: e.currentTarget.value })}
              />
              <TextInput
                label="אתר" disabled={locked || !canEdit} value={draft.site_name}
                onChange={(e) => update({ site_name: e.currentTarget.value })}
              />
            </Group>

            <Textarea
              label="הערות" autosize minRows={2} disabled={locked || !canEdit} value={draft.notes}
              onChange={(e) => update({ notes: e.currentTarget.value })}
            />

            <Group justify="space-between" mt="sm">
              <Title order={5}>שורות הניפוק</Title>
              {!locked && canEdit && (
                <Group gap="sm">
                  <Checkbox
                    size="xs"
                    label="כלול רכיבים שאינם פעילים (EOS/EOL)"
                    checked={includeRetired}
                    onChange={(e) => setIncludeRetired(e.currentTarget.checked)}
                  />
                  <Button
                    size="xs"
                    variant="light"
                    loading={expanding}
                    onClick={() => setPicking(true)}
                  >
                    בחר פריטים מהקטלוג
                  </Button>
                </Group>
              )}
            </Group>

            {draft.lines.length === 0 ? (
              <Text c="dimmed" size="sm">אין שורות. השתמש ב"בחר פריטים מהקטלוג".</Text>
            ) : (
              // Sideways scroll, not column-hiding — same reasoning as
              // `ReplacementPlanEditor`'s line table: every column here is
              // either an identifier or a live input, so there is nothing
              // low-priority to drop. Without the wrapper + `miw`, this
              // Modal going full-screen on a phone (`AppModal`) would hand
              // the table a 390px-wide box to lay 8 columns out in instead of
              // a sideways scrollbar — the same class of bug that put a 23px
              // input on screen before `ReplacementPlanEditor` got this.
              <Box style={{ overflowX: 'auto' }}>
                <Table withColumnBorders miw={950}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ ...STICKY_START, zIndex: 3 }}>מק"ט</Table.Th>
                      <Table.Th>תיאור</Table.Th>
                      <Table.Th w={90}>במלאי</Table.Th>
                      <Table.Th w={110}>כמות</Table.Th>
                      <Table.Th w={130}>עלות יח׳</Table.Th>
                      <Table.Th w={110}>תעריף</Table.Th>
                      <Table.Th w={120}>סה"כ</Table.Th>
                      <Table.Th style={{ ...STICKY_END, width: 40, zIndex: 3 }} />
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {draft.lines.map((line, index) => {
                      const onHand = stockBySku.get(line.sku)
                      const short = onHand !== undefined && onHand < line.qty
                      return (
                        <Table.Tr key={index}>
                          <Table.Td style={{ ...CODE_STYLE, ...STICKY_START, zIndex: 1 }}>{line.sku}</Table.Td>
                          <Table.Td>{line.description || EM_DASH}</Table.Td>
                          <Table.Td>
                            {/* Context, not a constraint — the desktop does
                                not block on stock either, because an
                                issuance can be raised before the goods land. */}
                            <Text size="sm" c={short ? 'orange' : undefined}>
                              {onHand === undefined ? EM_DASH : fmtQty(onHand)}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <NumberInput
                              size="xs" hideControls min={0} decimalScale={2} disabled={locked || !canEdit}
                              value={line.qty}
                              onChange={(v) => updateLine(index, { qty: Number(v) || 0 })}
                            />
                          </Table.Td>
                          <Table.Td>
                            <NumberInput
                              size="xs" hideControls min={0} decimalScale={2} thousandSeparator=","
                              disabled={locked || !canEdit} value={line.unit_cost}
                              onChange={(v) => updateLine(index, { unit_cost: Number(v) || 0 })}
                            />
                          </Table.Td>
                          <Table.Td>
                            {/* Stored as a fraction, shown as a percentage. */}
                            <NumberInput
                              size="xs" hideControls min={0} max={100} suffix="%" decimalScale={2}
                              disabled={locked || !canEdit}
                              value={Number((line.line_tariff_pct * 100).toFixed(2))}
                              onChange={(v) =>
                                updateLine(index, { line_tariff_pct: (Number(v) || 0) / 100 })
                              }
                            />
                          </Table.Td>
                          <Table.Td style={{ whiteSpace: 'nowrap' }}>
                            {fmtMoney(line.qty * line.unit_cost * (1 + (line.line_tariff_pct || 0)),
                                      draft.currency)}
                          </Table.Td>
                          <Table.Td style={{ ...STICKY_END, zIndex: 2 }}>
                            {!locked && canEdit && (
                              <ActionIcon
                                size={30} color="red" variant="subtle" aria-label="הסר שורה"
                                onClick={() =>
                                  setDraft((d) =>
                                    d ? { ...d, lines: d.lines.filter((_, i) => i !== index) } : d)
                                }
                              >
                                ✕
                              </ActionIcon>
                            )}
                          </Table.Td>
                        </Table.Tr>
                      )
                    })}
                  </Table.Tbody>
                </Table>
              </Box>
            )}

            <Group justify="flex-end" gap="lg">
              <Text size="sm" c="dimmed">
                ציוד {fmtMoney(totals.equipment, draft.currency)} · תעריף{' '}
                {fmtMoney(totals.tariff, draft.currency)}
              </Text>
              <Text fw={700}>סה"כ {fmtMoney(totals.total, draft.currency)}</Text>
            </Group>
            <Text size="xs" c="dimmed" ta="end">
              הסכומים מחושבים מחדש בשרת בעת השמירה.
            </Text>

            <Group justify="flex-end">
              <Button variant="default" onClick={onClose}>סגור</Button>
              {!locked && canEdit && (
                <Button onClick={submit} loading={save.isPending}>שמור טיוטה</Button>
              )}
            </Group>
          </Stack>
        )}
      </Modal>

      <ItemPicker
        opened={picking}
        onClose={() => setPicking(false)}
        onPick={(items) => void addItems(items)}
        title="בחירת פריטים לניפוק"
        confirmLabel="הוסף לניפוק"
      />
    </>
  )
}
